import React, { Component } from 'react';
import PropTypes from 'prop-types';
import numeral from 'numeral';
import { withStyles } from '@glu/theming';
import moment from 'moment';

import get from 'lodash/get';
import styles from '../createPayment/createPayment.styles';

// store
import { setPaymentActionMessages, setPaymentData } from '../../logic/store/payments/actions';

// utils
import AppPropTypes from '../../proptypes/AppPropTypes';
import { WORKSPACES, PAYMENT_ACTIONS, PAYMENT_TYPES } from '../../constants';
import { buildBankCode } from '../wirePayment/utils';

// api
import { retrieveWirePayment } from '../../api/payments/service';
import { validateWirePayment } from '../createPayment/createPayment.api';

// components
import CreatePaymentForm from '../createPayment/CreatePaymentForm';
import withLocale from '../withLocale/withLocale';
import Workflow from '../workflow/Workflow';
import Currency from '../wirePayment/components/Currency';
import { PlaceResolverComponent as PlaceResolver, variants } from '../placeResolver/PlaceResolver';
import withRouterConnect from '../withRouterConnect/withRouterConnect';
import { buildAppropriateViewComponent } from '../createPayment/CreatePayment.utils';
import WireContext from '../wirePayment/WireContext';

export class ModifyPaymentComponent extends Component {
  state = {
    error: {
      thrown: false
    },
    submissionDisabled: false,
    // This is a way to lift state from inputs from CreatePaymentForm so we can display
    wireContextState: {
      data: {},
      getCurrency: () => get(this.state, 'wireContextState.data.currency'),
      updateData: (value) => {
        this.setState((state) => ({
          wireContextState: {
            ...state.wireContextState,
            data: {
              ...state.wireContextState.data,
              ...value
            }
          }
        }));
      }
    }
  };

  static defaultProps = {
    location: {},
    classes: {},
    match: {},
    locale: {},
    payment: {},
    history: {},
    setPaymentActionMessages() {},
    onSubmission() {},
    setSubscriptions() {}
  };

  static propTypes = {
    location: PropTypes.shape({
      state: PropTypes.shape({})
    }),
    locale: AppPropTypes.locale,
    classes: AppPropTypes.jssClasses,
    payment: PropTypes.shape({}),
    setPaymentActionMessages: PropTypes.func,
    match: PropTypes.shape({
      params: PropTypes.shape({
        bankTrace: PropTypes.number
      })
    }),
    setSubscriptions: PropTypes.func,
    onSubmission: PropTypes.func,
    history: PropTypes.shape({
      location: PropTypes.shape({
        state: PropTypes.shape({
          payments: PropTypes.arrayOf(
            PropTypes.shape({
              bankTrace: PropTypes.string,
              amount: PropTypes.string
            })
          )
        })
      })
    })
  };

  setIsSubmitting = (isSubmitting) => this.setState(() => ({ isSubmitting }));

  /**
   * Invoked when amount is changed
   * @param {number} currencyAmount
   * @returns {void}
   */
  onChange = (currencyAmount, currency) => {
    this.setState({ currencyAmount, currency });
  };

  getWirePayment = () => {
    const { bankTrace } = this.props.match.params;
    return retrieveWirePayment({ bankTrace });
  };

  /**
   * Build props used with @Workflow component
   * @returns {object}
   */
  buildWorkflowProps = (data) => {
    const paymentAmount = data.creditAmount || data.amount;
    const {
      classes: { currentAmount },
      history,
      locale,
      location
    } = this.props;
    const { currencyAmount } = this.state;

    const workflowProps = {
      title: locale['modify.payment.title'],
      onBack: () => {
        history.push({ pathname: get(location, 'state.workflowStartPath', WORKSPACES.PYMTCNTR) });
      }
    };

    if (currencyAmount || paymentAmount) {
      const { wireContextState } = this.state;
      const { data } = wireContextState;
      const exchangeRate = Number(data.exchangeRate || 1);
      const amount = data.amount ? numeral(data.amount).value().toFixed(2) : '0.00';
      const convertedAmount =
        get(data, 'amountType[0].type') === 'D'
          ? // USD Equivalent Calculation
            (amount / exchangeRate).toFixed(2)
          : // Foreign Amount Calculation
            amount;
      const displayAmount =
        get(data, 'currency[0].currency') && get(data, 'currency[0].currency') !== 'USD'
          ? `${numeral(convertedAmount).format('0,0.00')} ${get(data, 'currency[0].currency')}`
          : `$${numeral(amount).format('0,0.00')}`;

      workflowProps.extra = (
        <div className={currentAmount}>
          <span className="amount">{displayAmount}</span>
        </div>
      );
    }

    return workflowProps;
  };

  onFormSave = ({ fields, options }) => {
    const { history, locale, onSubmission, setSubscriptions } = this.props;

    this.setState((state) => ({ ...state, submissionDisabled: true }));

    return validateWirePayment({
      form: fields,
      history,
      locale,
      options,
      setSubscriptions,
      onSubmission,
      action: PAYMENT_ACTIONS.MODIFY_PAYMENT,
      onError: ({ error }) => {
        this.setIsSubmitting(false);
        this.setState({ submissionDisabled: false, error });
      },
      callback: ({ fn }) => this.setState({ submissionDisabled: false, error: false }, fn)
    });
  };

  clearError = () => {
    this.setState({ error: {} });
  };

  render() {
    const {
      classes: { paymentForm, section },
      locale,
      location
    } = this.props;
    const { error, submissionDisabled } = this.state;

    return (
      <PlaceResolver services={[this.getWirePayment]} variant={variants.spinner}>
        {({ results: [payment] }) => {
          if (!payment) return null;
          return (
            <WireContext.Provider value={this.state.wireContextState}>
              <Workflow {...this.buildWorkflowProps(payment)}>
                <section className={section}>
                  <CreatePaymentForm
                    setIsSubmitting={this.setIsSubmitting}
                    isSubmitting={this.state.isSubmitting}
                    className={paymentForm}
                    action={PAYMENT_ACTIONS.MODIFY_PAYMENT}
                    preloadFields={payment}
                    error={error}
                    clearError={this.clearError}
                    onDiscard={{
                      returnUrl: get(location, 'state.workflowStartPath', WORKSPACES.PYMTCNTR)
                    }}
                    onParentUpdate={this.onChange}
                    submissionDisabled={submissionDisabled}
                    fromTemplate={!!payment.templateCode}
                    footer={{
                      showTotal: true,
                      submit: {
                        fn: this.onFormSave,
                        label: locale['common.confirm']
                      }
                    }}
                    topSectionContent={({ formChange, formState, options }) => {
                      const paymentType = get(formState, 'paymentType.0.type');
                      const isInternational = paymentType === PAYMENT_TYPES.WIRE.INTERNATIONAL;

                      if (!isInternational) {
                        return null;
                      }

                      return (
                        <Currency
                          data={options}
                          formState={formState}
                          onParentUpdate={this.onChange}
                          formChange={formChange}
                        />
                      );
                    }}
                    preloaded
                  >
                    {({
                      action,
                      data,
                      events,
                      form,
                      formState,
                      fromTemplate,
                      retrievedData,
                      services
                    }) => {
                      if (!form.paymentType) return null;

                      const BBI = get(form, 'beneficiaryBankInfo');
                      const IntBankInfo1 = get(form, 'intermediaryBankInfo1');
                      const IntBankInfo2 = get(form, 'intermediaryBankInfo2');
                      const isDefined = (o) => o && Object.values(o).filter(Boolean).length > 0;

                      const buildTransactionDate = (date) => {
                        if (Array.isArray(date)) {
                          return [moment(date[0]), moment(date[0])];
                        }

                        return [moment(date), moment(date)];
                      };
                      let paymentType;
                      if (form.paymentType === 'Domestic')
                        paymentType = [{ id: '101', name: 'Domestic Wire', type: 'Domestic' }];
                      if (form.paymentType === 'International')
                        paymentType = [
                          { id: '102', name: 'International Wire', type: 'International' }
                        ];
                      const source = {
                        ...form,
                        transactionDate: buildTransactionDate(form.transactionDate),
                        paymentType,
                        currency: data.fxRates.find((x) => x.currency === form.currency),
                        beneficiaryBankInfo:
                          isDefined(BBI) && !Array.isArray(BBI)
                            ? [
                                buildBankCode({
                                  routingNumber: get(BBI, 'routingNumber'),
                                  bankCode: get(BBI, 'bankCode'),
                                  bankName: get(BBI, 'bankName'),
                                  paymentMethod: get(BBI, 'paymentMethod')
                                })
                              ]
                            : (isDefined(BBI) && form.beneficiaryBankInfo) || null,
                        intermediaryBankInfo1:
                          isDefined(IntBankInfo1) && !Array.isArray(IntBankInfo1)
                            ? [
                                buildBankCode({
                                  routingNumber: get(IntBankInfo1, 'routingNumber'),
                                  bankCode: get(IntBankInfo1, 'bankCode'),
                                  bankName: get(IntBankInfo1, 'bankName'),
                                  paymentMethod: get(IntBankInfo1, 'paymentMethod')
                                })
                              ]
                            : (isDefined(IntBankInfo1) && form.intermediaryBankInfo1) || null,
                        intermediaryBankInfo2:
                          isDefined(IntBankInfo2) && !Array.isArray(IntBankInfo2)
                            ? [
                                buildBankCode({
                                  routingNumber: get(IntBankInfo2, 'routingNumber'),
                                  bankCode: get(IntBankInfo2, 'bankCode'),
                                  bankName: get(IntBankInfo2, 'bankName'),
                                  paymentMethod: get(IntBankInfo2, 'paymentMethod')
                                })
                              ]
                            : (isDefined(IntBankInfo2) && form.intermediaryBankInfo2) || null
                      };

                      return buildAppropriateViewComponent(source, {
                        services,
                        formState,
                        action,
                        fromTemplate,
                        retrievedData,
                        ...data,
                        ...events
                      });
                    }}
                  </CreatePaymentForm>
                </section>
              </Workflow>
            </WireContext.Provider>
          );
        }}
      </PlaceResolver>
    );
  }
}

const Styled = withStyles(styles)(ModifyPaymentComponent);
const Localized = withLocale(Styled);

/* istanbul ignore next */
const mapDispatchToProps = (dispatch) => ({
  onSubmission: (payment) => dispatch(setPaymentData(payment)),
  setPaymentActionMessages: (paymentActionMessages) =>
    dispatch(setPaymentActionMessages(paymentActionMessages))
});

export default withRouterConnect(null, mapDispatchToProps)(Localized);
